import {
  MentorProfileDraft,
  useUpdateMentorProfileDraft,
} from "@/lib/api/mentor-profile";
import {
  maxFurthestStep,
  type WizardFurthestStep,
} from "@/lib/mentor-profile/wizard-steps";
import { useCallback, useEffect, useRef, useState } from "react";

const AUTOSAVE_DELAY_MS = 700;

export type DraftSaveState = "idle" | "saving" | "saved";

export interface UseMentorProfileDraftResult {
  draft: MentorProfileDraft;
  saveState: DraftSaveState;
  furthestStep: string | null;
  setField: <K extends keyof MentorProfileDraft>(
    key: K,
    value: MentorProfileDraft[K],
  ) => void;
  setFields: (patch: Partial<MentorProfileDraft>) => void;
  markFurthestStep: (step: WizardFurthestStep) => void;
  flush: () => void;
}

export function useMentorProfileDraft(
  initialDraft: MentorProfileDraft,
  initialFurthestStep: string | null = null,
): UseMentorProfileDraftResult {
  const [draft, setDraft] = useState<MentorProfileDraft>(initialDraft);
  const [saveState, setSaveState] = useState<DraftSaveState>("idle");
  const [furthestStep, setFurthestStep] = useState<string | null>(
    initialFurthestStep,
  );
  const { mutate } = useUpdateMentorProfileDraft();

  const pendingRef = useRef<Partial<MentorProfileDraft>>({});
  const furthestPendingRef = useRef<string | null>(null);
  const furthestStateRef = useRef<string | null>(initialFurthestStep);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    const patch = pendingRef.current;
    const nextFurthest = furthestPendingRef.current;
    if (Object.keys(patch).length === 0 && !nextFurthest) return;
    pendingRef.current = {};
    furthestPendingRef.current = null;
    mutate(
      {
        draft: patch,
        ...(nextFurthest ? { furthestStep: nextFurthest } : {}),
      },
      {
        onSuccess: (review) => {
          setSaveState("saved");
          if (review.furthest_step) {
            const next = maxFurthestStep(
              furthestStateRef.current,
              review.furthest_step as WizardFurthestStep,
            );
            setFurthestStep(next);
            furthestStateRef.current = next;
          }
          // Server-derived fields (e.g. Spotify enrichment) aren't edited
          // directly, so merge them back so the live preview updates without a
          // reload.
          setDraft((prev) => ({
            ...prev,
            spotify_meta: review.draft.spotify_meta,
            spotify_preview_url: review.draft.spotify_preview_url,
          }));
        },
      },
    );
  }, [mutate]);

  const scheduleFlush = useCallback(() => {
    setSaveState("saving");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
  }, [flush]);

  const markFurthestStep = useCallback(
    (step: WizardFurthestStep) => {
      const next = maxFurthestStep(furthestStateRef.current, step);
      if (next === furthestStateRef.current) return;
      furthestStateRef.current = next;
      furthestPendingRef.current = next;
      setFurthestStep(next);
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const setField = useCallback(
    <K extends keyof MentorProfileDraft>(
      key: K,
      value: MentorProfileDraft[K],
    ) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
      pendingRef.current[key] = value;
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const setFields = useCallback(
    (patch: Partial<MentorProfileDraft>) => {
      setDraft((prev) => ({ ...prev, ...patch }));
      Object.assign(pendingRef.current, patch);
      scheduleFlush();
    },
    [scheduleFlush],
  );

  useEffect(() => () => flush(), [flush]);

  return {
    draft,
    saveState,
    furthestStep,
    setField,
    setFields,
    markFurthestStep,
    flush,
  };
}
